import { auxApi, rundownApi, runnerApi, teamApi, billingApi, eventApi } from './axios'

let triggerToast = null

export function injectAddToast(addToast) {
  triggerToast = addToast
}

async function _errorHandler(promise, { toast = true, silenceStatuses = [] } = {}) {
  try {
    return await promise
  } catch (err) {
    const status = err.response?.status
    if (toast && !silenceStatuses.includes(status))
      triggerToast({
        title: String(err.response?.data?.message || err.response?.data || err),
        body: [`Server responded with status ${status}`, err.response?.statusText].filter(Boolean).join(' - '),
        type: 'fail',
      })
    throw err
  }
}

//
// RUNDOWN
//

export const getRundownsByTeam = async (teamId) => {
  return await _errorHandler(rundownApi.get(`/get-by-team/${teamId}`))
}

export const getTemplatesByTeam = async (teamId) => {
  return await _errorHandler(rundownApi.get(`/get-templates-by-team/${teamId}`))
}

export const createRundown = async (body) => {
  return await _errorHandler(rundownApi.post('/', body))
}

export const getRundown = async (rundownId) => {
  return await _errorHandler(rundownApi.get(`/${rundownId}`))
}
export const getRundownEvent = async (rundownId, { toast }) => {
  return await _errorHandler(rundownApi.get(`/${rundownId}/event`), { toast })
}

export const updateRundown = async (rundownId, update) => {
  return await _errorHandler(rundownApi.patch(`/${rundownId}`, update))
}

export const cloneRundown = async (rundownId, teamId) => {
  return await _errorHandler(rundownApi.post(`/${rundownId}/clone`, { teamId }))
}

export const updateRundownCue = async (rundownId, cueId, update, isFirstCue = false) => {
  return await _errorHandler(
    rundownApi.patch(`/${rundownId}/cues/${cueId}`, update, {
      params: { isFirstCue },
    }),
  )
}

export const updateRundownCues = async (rundownId, cueIds, update) => {
  return await _errorHandler(rundownApi.patch(`/${rundownId}/cues`, { cueIds, update: update }))
}

export const addRundownCue = async (rundownId, params = {}, data = {}) => {
  return await _errorHandler(rundownApi.post(`/${rundownId}/cues`, data, { params }))
}

export const duplicateRundownCue = async (rundownId, cueId) => {
  return await _errorHandler(rundownApi.post(`/${rundownId}/cues/${cueId}/clone`))
}

export const duplicateRundownCues = async (rundownId, cueIds) => {
  return await _errorHandler(rundownApi.post(`/${rundownId}/cues/clone`, { cueIds }))
}

export const deleteRundownCue = async (rundownId, cueId) => {
  return await _errorHandler(rundownApi.delete(`/${rundownId}/cues/${cueId}`))
}

export const deleteRundownCues = async (rundownId, cueIds) => {
  return await _errorHandler(rundownApi.delete(`/${rundownId}/cues`, { data: { cueIds } }))
}

export const restoreRundownCue = async (rundownId, cueId) => {
  return await _errorHandler(rundownApi.post(`/${rundownId}/cues/${cueId}/restore`))
}

export const getDeletedRundownCues = async (rundownId) => {
  return await _errorHandler(rundownApi.get(`/${rundownId}/cues/deleted`))
}

export const restoreRundownColumn = async (rundownId, columnId) => {
  return await _errorHandler(rundownApi.post(`/${rundownId}/columns/${columnId}/restore`))
}

export const getDeletedRundownColumns = async (rundownId) => {
  return await _errorHandler(rundownApi.get(`/${rundownId}/columns/deleted`))
}

export const reorderRundownCues = async (rundownId, cues) => {
  return await _errorHandler(rundownApi.patch(`/${rundownId}/reorderCues`, cues))
}

export const moveRundownCuesAfter = async (rundownId, sourceCueIds, targetCueId) => {
  return await _errorHandler(
    rundownApi.post(`/${rundownId}/cues/move-after`, {
      sourceCueIds,
      targetCueId,
    }),
  )
}

export const groupRundownCues = async (rundownId, beforeCueId, cuesToGroupIds) => {
  return await _errorHandler(
    rundownApi.patch(`/${rundownId}/groupCues`, {
      beforeCueId,
      cuesToGroupIds,
    }),
  )
}

export const unsetRundownRunner = async (rundownId) => {
  return await _errorHandler(rundownApi.post(`/${rundownId}/unset-runner`))
}

export const createRundownRunner = async (rundownId) => {
  return await _errorHandler(rundownApi.post(`/${rundownId}/create-runner`))
}

export const deleteRundown = async (rundownId) => {
  return await _errorHandler(rundownApi.delete(`/${rundownId}`))
}

export const createRundownSignature = async (rundownId, text) => {
  return await _errorHandler(rundownApi.post(`/${rundownId}/create-signature`, { text }))
}

export const revokeRundownSignatures = async (rundownId) => {
  return await _errorHandler(rundownApi.post(`/${rundownId}/revoke-signatures`))
}

export const uploadRundownFile = async (rundownId, file, onProgress = () => {}) => {
  const formData = new FormData()
  formData.append('file', file)
  const config = {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (event) => onProgress(Math.round((event.loaded / event.total) * 100)),
  }
  return await _errorHandler(rundownApi.post(`/${rundownId}/upload-file`, formData, config))
}

export const rundownPlan = async (rundownId, { toast }) => {
  return await _errorHandler(rundownApi.get(`/${rundownId}/plan`), { toast })
}

export const generatePdf = async (
  rundownId,
  orientation,
  { columnsToExport, textSize, alternateRowColors, showElapsedTimes } = {},
) => {
  return await _errorHandler(
    rundownApi.get(`/${rundownId}/export-pdf`, {
      params: {
        orientation,
        columnsToExport,
        textSize,
        alternateRowColors,
        showElapsedTimes,
      },
    }),
  )
}

export const generateHtml = async (
  rundownId,
  { columnsToExport, textSize, alternateRowColors, showElapsedTimes } = {},
) => {
  return await _errorHandler(
    rundownApi.get(`/${rundownId}/export-html`, {
      params: {
        columnsToExport,
        textSize,
        alternateRowColors,
        showElapsedTimes,
      },
    }),
  )
}

export const generateCsv = async (rundownId) => {
  return await _errorHandler(rundownApi.get(`/${rundownId}/export-csv`, { params: {} }))
}

export const createPrompter = async (rundownId) => {
  return await _errorHandler(rundownApi.post(`/${rundownId}/prompter`))
}

//
// RUNNER
//

export const startRunnerTimesnap = async (rundownId, runnerId, cueId) => {
  return await _errorHandler(
    runnerApi.post(`/${rundownId}/${runnerId}/timesnap/start`, null, {
      params: { cueId },
    }),
  )
}

export const startRunnerTimesnapOnPreviousCue = async (rundownId, runnerId) => {
  return await _errorHandler(runnerApi.post(`/${rundownId}/${runnerId}/timesnap/previous`))
}

export const pauseRunnerTimesnap = async (rundownId, runnerId) => {
  return await _errorHandler(runnerApi.post(`/${rundownId}/${runnerId}/timesnap/pause`))
}

export const endRunnerTimesnap = async (rundownId, runnerId) => {
  return await _errorHandler(runnerApi.post(`/${rundownId}/${runnerId}/timesnap/end`))
}

export const updateRunner = async (rundownId, runnerId, update) => {
  return await _errorHandler(runnerApi.put(`/${rundownId}/${runnerId}`, update))
}

//
// TEAM
//

export const teamsByUid = async (uid) => {
  return await _errorHandler(teamApi.get(`/teams-by-uid/${uid}`))
}

export const getTeam = async (teamId) => {
  return await _errorHandler(teamApi.get(`/${teamId}`))
}

export const updateTeam = async (teamId, update) => {
  return await _errorHandler(teamApi.patch(`/${teamId}`, update))
}

export const membersMetadata = async (teamId) => {
  return await _errorHandler(teamApi.get(`/${teamId}/members/meta`))
}

export const inviteTeamMember = async (teamId, teamMember) => {
  return await _errorHandler(teamApi.post(`/${teamId}/members`, teamMember))
}

export const acceptTeamInvite = async (teamId) => {
  return await _errorHandler(teamApi.post(`/${teamId}/accept-invite`))
}

export const resendInvite = async (teamId, email) => {
  return await _errorHandler(teamApi.post(`/${teamId}/resend-invite`, { email }))
}

export const removeTeamMember = async (teamId, emailOrUid) => {
  return await _errorHandler(teamApi.delete(`/${teamId}/members/${emailOrUid}`))
}

export const getTeamPlan = async (teamId) => {
  return await _errorHandler(teamApi.get(`/${teamId}/plan`))
}

export const getTeamTransactions = async (teamId) => {
  return await _errorHandler(teamApi.get(`/${teamId}/transactions`))
}

export const listApiTokens = async (teamId) => {
  return await _errorHandler(teamApi.get(`/${teamId}/api-tokens`))
}

export const mintApiToken = async (teamId, payload) => {
  return await _errorHandler(teamApi.post(`/${teamId}/api-tokens`, payload))
}

export const revokeApiToken = async (teamId, jti) => {
  return await _errorHandler(teamApi.delete(`/${teamId}/api-tokens/${jti}`))
}

export const getApiTokenValue = async (teamId, jti) => {
  return await _errorHandler(teamApi.get(`/${teamId}/api-tokens/${jti}/value`))
}

export const cancelSubscription = async (teamId, subscriptionId) => {
  return await _errorHandler(teamApi.post(`/${teamId}/subscriptions/${subscriptionId}/cancel`))
}

//
// AUX
//

export const getValidCode = async (codeId) => {
  return await _errorHandler(auxApi.get(`/code/${codeId}`))
}

export const createCode = async (url) => {
  return await _errorHandler(auxApi.post('/code', { url }))
}

export const emailGuest = async (email, url) => {
  return await _errorHandler(auxApi.post('/email-guest', { email, url }))
}

export const updateAirtable = async (data) => {
  return await _errorHandler(auxApi.post('/update-airtable', data))
}

export const newsletterSubscribe = async () => {
  return await _errorHandler(auxApi.post('/newsletter-subscribe'))
}

//
// BILLING
//

export const getTransactions = async (transactionId) => {
  return await _errorHandler(billingApi.get(`/transactions?transactionId=${transactionId}`))
}

export const getTransactionInvoice = async (transactionId) => {
  return await _errorHandler(billingApi.post(`/transactions/${transactionId}/invoice`))
}

//
// EVENT
//

export const getEventsByTeam = async (teamId) => {
  return await _errorHandler(eventApi.get(`/get-by-team/${teamId}`))
}

export const updateEvent = async (eventId, update) => {
  return await _errorHandler(eventApi.patch(`/${eventId}`, update))
}

export const createEvent = async (body) => {
  return await _errorHandler(eventApi.post('/', body))
}

export const getEvent = async (eventId, params) => {
  return await _errorHandler(eventApi.get(`/${eventId}`, { params }))
}

export const getEventRundowns = async (eventId, params) => {
  return await _errorHandler(eventApi.get(`/${eventId}/rundowns`, { params }))
}
export const getEventRundownLinks = async (eventId, params) => {
  return await _errorHandler(eventApi.get(`/${eventId}/rundown-links`, { params }))
}

export const uploadEventFile = async (eventId, file, onProgress = () => {}) => {
  const formData = new FormData()
  formData.append('file', file)
  const config = {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (event) => onProgress(Math.round((event.loaded / event.total) * 100)),
  }
  return await _errorHandler(eventApi.post(`/${eventId}/upload-file`, formData, config))
}

export const deleteEvent = async (eventId) => {
  return await _errorHandler(eventApi.delete(`/${eventId}`))
}

export const createEventSignature = async (eventId, text) => {
  return await _errorHandler(eventApi.post(`/${eventId}/create-signature`, { text }))
}

export const revokeEventSignatures = async (eventId) => {
  return await _errorHandler(eventApi.post(`/${eventId}/revoke-signatures`))
}

export const duplicateEvent = async (eventId) => {
  // Matches the event function's 90s timeout: a big event clone runs past the 30s default
  return await _errorHandler(eventApi.post(`/${eventId}/clone`, null, { timeout: 90_000 }))
}
